import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { copyTextToClipboard, formatRubPrice, formatUsdtPrice } from "../visoria/helpers";
import { createAccountOrdersFeed } from "./account-orders-feed";
import { useAccountView } from './AccountViewContext';
import AccountOrderItems from './AccountOrderItems';

export function formatOrderAmount(order) {
  const currency = order.currency || (order.source === 'bot' ? '' : 'RUB');
  const amount = order.amount ?? order.amountRub;
  if (amount == null || !Number.isFinite(Number(amount))) return '—';
  if (currency === 'USDT') return formatUsdtPrice(amount);
  return currency === 'RUB' ? formatRubPrice(amount) : `${Number(amount).toLocaleString('ru-RU')} ${currency}`.trim();
}

function formatDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });
}

function formatDeadline(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const time = date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  return date.toDateString() === new Date().toDateString()
    ? time
    : `${date.toLocaleDateString("ru-RU", { day: "numeric", month: "long" })}, ${time}`;
}

function keyText(item) {
  return typeof item === "string" ? item : item?.key || "";
}

export function OrderKeys({ order, onUnauthorized }) {
  const {api}=useAccountView();
  const suppliedKeys = Array.isArray(order.keys) ? order.keys : null;
  const [state, setState] = useState({ loading: true, keys: [], error: "" });
  const [copiedKey, setCopiedKey] = useState("");

  useEffect(() => {
    // На странице деталей коды уже пришли вместе с заказом. В списке их нет.
    if (suppliedKeys !== null) return undefined;
    let cancelled = false;
    api
      .getHistoryOrder(order)
      .then((order) => {
        if (!cancelled) setState({ loading: false, keys: order?.keys || [], error: "" });
      })
      .catch((error) => {
        if (!cancelled && error.status === 401) onUnauthorized?.();
        if (!cancelled) setState({ loading: false, keys: [], error: error.message });
      });
    return () => {
      cancelled = true;
    };
  }, [order, onUnauthorized, api, suppliedKeys]);

  const handleCopy = async (key) => {
    if (await copyTextToClipboard(key)) {
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(""), 2000);
    }
  };

  if (suppliedKeys === null && state.loading) return <p className="other-store-codes-status">Загружаем коды…</p>;
  if (suppliedKeys === null && state.error) return <p className="other-store-error">{state.error}</p>;
  const keys = suppliedKeys ?? state.keys;
  if (!keys.length) return <p className="other-store-codes-status">Кодов пока нет.</p>;

  return (
    <ul className="other-store-keys-list other-store-keys-list--compact account-keys">
      {keys.map((item, index) => {
        const key = keyText(item);
        return (
          <li key={`${key}-${index}`} className="other-store-key-item">
            <code>{key}</code>
            <button type="button" className="other-store-key-copy" onClick={() => handleCopy(key)}>
              {copiedKey === key ? "Скопировано" : "Скопировать"}
            </button>
            {item?.productName ? (
              <span className="other-store-key-meta">{item.productName}</span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function OrderItem({ order, keysOpen, onToggleKeys, onUnauthorized }) {
  const {readOnly,orderPath}=useAccountView();
  return (
    <li className={`account-order${order.status === "unpaid" ? " account-order--unpaid" : ""}`}>
      <div className="account-order-head">
        <span className="account-order-date">{formatDate(order.createdAt)}</span>
        <span className={`account-order-status account-order-status--${order.status}`}>
          {order.statusLabel}
        </span>
      </div>
      <p className="account-order-source">{order.shopLabel} · {order.channelLabel}</p>
      <AccountOrderItems items={order.items || []} />
      {order.status === "pending" && order.paymentExpiresAt ? (
        <p className="account-order-deadline">Оплатить до {formatDeadline(order.paymentExpiresAt)}</p>
      ) : null}
      <div className="account-order-foot">
        <span className="account-order-amount">{formatOrderAmount(order)}</span>
        <div className="account-order-actions">
          {order.status === "ready" ? (
            <button type="button" className="account-link-btn" onClick={onToggleKeys}>
              {keysOpen ? "Скрыть коды" : "Показать коды"}
            </button>
          ) : null}
          <Link to={orderPath(order)} className="account-link-btn">
            {!readOnly && order.status === "pending" ? "Оплатить" : "Открыть заказ"}
          </Link>
        </div>
      </div>
      {keysOpen ? <OrderKeys order={order} onUnauthorized={onUnauthorized} /> : null}
    </li>
  );
}

function Curtain({ id, open, children }) {
  const ref = useRef(null);
  useEffect(() => {
    // React 18 не знает атрибут inert: свёрнутые заказы не должны получать фокус с клавиатуры
    if (ref.current) ref.current.inert = !open;
  });
  return (
    <div id={id} ref={ref} className={`account-orders-curtain${open ? " is-open" : ""}`} aria-hidden={!open}>
      <div>{children}</div>
    </div>
  );
}

const PREVIEW_COUNT = 5;
const UNPAID_LABEL = "Не оплачен";
const MAX_TIMER_MS = 60 * 60 * 1000;

const isExpired = (order, now) =>
  order.status === "pending" && order.paymentExpiresAt && new Date(order.paymentExpiresAt).getTime() <= now;

/** Ожидающие заказы с истёкшим сроком оплаты уходят в неоплаченные, даже если страница открыта. */
function useExpiredPending(orders, setOrders, setUnpaid) {
  useEffect(() => {
    const deadlines = orders
      .filter((order) => order.status === "pending" && order.paymentExpiresAt)
      .map((order) => new Date(order.paymentExpiresAt).getTime())
      .filter(Number.isFinite);
    if (!deadlines.length) return undefined;
    const delay = Math.min(Math.max(Math.min(...deadlines) - Date.now(), 0), MAX_TIMER_MS);
    const timer = window.setTimeout(() => {
      const now = Date.now();
      const expired = orders.filter((order) => isExpired(order, now));
      if (!expired.length) return setOrders((current) => [...current]);
      setOrders((current) => current.filter((order) => !isExpired(order, now)));
      setUnpaid((current) => [
        ...expired.map((order) => ({ ...order, status: "unpaid", statusLabel: UNPAID_LABEL, paymentExpiresAt: null })),
        ...current,
      ]);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [orders, setOrders, setUnpaid]);
}

function AccountOrders({ email, onUnauthorized }) {
  const {api}=useAccountView();
  const [orders, setOrders] = useState([]);
  const [unpaid, setUnpaid] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openOrderId, setOpenOrderId] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [unpaidOpen, setUnpaidOpen] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const feed = useRef(null);
  const generation = useRef(0);
  const busy = useRef(false);

  useExpiredPending(orders, setOrders, setUnpaid);

  const loadPage = useCallback(
    async (nextPage) => {
      setLoading(true);
      setError("");
      if (busy.current) return;
      busy.current = true;
      const currentGeneration = generation.current;
      try {
        if (nextPage === 1) {
          feed.current = createAccountOrdersFeed(api);
          setOrders([]); setUnpaid([]); setOpenOrderId(''); setWarnings([]);
        }
        const data = await feed.current.next();
        if (currentGeneration !== generation.current) return;
        setOrders((current) => (nextPage === 1 ? data.orders : [...current, ...data.orders]));
        setUnpaid(data.unpaid || []);
        setWarnings(data.unavailableSources || []);
        setPage(nextPage);
        setHasMore(Boolean(data.hasMore));
      } catch (loadError) {
        if (currentGeneration !== generation.current) return;
        if (loadError.status === 401) {
          setOrders([]); setUnpaid([]); setOpenOrderId('');
          onUnauthorized();
          return;
        }
        setError(loadError.message);
      } finally {
        if (currentGeneration === generation.current) { busy.current = false; setLoading(false); }
      }
    },
    [onUnauthorized,api],
  );

  useEffect(() => {
    generation.current += 1;
    busy.current = false;
    setExpanded(false); setUnpaidOpen(false);
    loadPage(1);
    return () => { generation.current += 1; };
  }, [loadPage, email]);

  const hasRest = orders.length > PREVIEW_COUNT || hasMore;
  const renderOrder = (order) => (
    <OrderItem
      key={order.historyKey}
      order={order}
      keysOpen={openOrderId === order.historyKey}
      onToggleKeys={() =>
        setOpenOrderId((current) => (current === order.historyKey ? "" : order.historyKey))
      }
      onUnauthorized={onUnauthorized}
    />
  );

  return (
    <div className="account-orders">
      <h2 className="other-store-purchase-title">Мои заказы</h2>
      {warnings.length > 0 ? <div className="account-history-notice" role="status">
        Часть истории временно недоступна. Показаны заказы доступных магазинов.{" "}
        <button type="button" className="account-link-btn" disabled={loading} onClick={() => loadPage(1)}>Обновить историю</button>
      </div> : null}
      {!loading && !error && !warnings.length && orders.length === 0 && unpaid.length === 0 ? (
        <p className="other-store-description">
          Заказов пока нет. Заказы, оформленные на {email}, появятся здесь сами.
        </p>
      ) : null}
      <ul className="account-order-list">
        {orders.slice(0, PREVIEW_COUNT).map(renderOrder)}
      </ul>
      {hasRest ? (
        <Curtain id="account-orders-rest" open={expanded}>
          <ul className="account-order-list">{orders.slice(PREVIEW_COUNT).map(renderOrder)}</ul>
          {hasMore && !loading ? (
            <button
              type="button"
              className="account-secondary-btn mb-3"
              onClick={() => loadPage(page + 1)}
            >
              Показать ещё
            </button>
          ) : null}
        </Curtain>
      ) : null}
      {loading ? <p className="other-store-codes-status">Загружаем заказы…</p> : null}
      {error ? <p className="other-store-error">{error}{" "}<button type="button" className="account-link-btn" disabled={loading} onClick={() => loadPage(1)}>Повторить</button></p> : null}
      {hasRest || unpaid.length ? (
        <div className="account-orders-buttons">
          {hasRest ? (
            <button
              type="button"
              className="account-secondary-btn"
              aria-expanded={expanded}
              aria-controls="account-orders-rest"
              onClick={() => setExpanded((current) => !current)}
            >
              {expanded ? "Свернуть заказы" : "Посмотреть все заказы"}
            </button>
          ) : null}
          {unpaid.length ? (
            <button
              type="button"
              className="account-secondary-btn"
              aria-expanded={unpaidOpen}
              aria-controls="account-orders-unpaid"
              onClick={() => setUnpaidOpen((current) => !current)}
            >
              {unpaidOpen ? "Скрыть неоплаченные" : "Неоплаченные"}{" "}
              <span className="account-unpaid-count">{unpaid.length}</span>
            </button>
          ) : null}
        </div>
      ) : null}
      {unpaid.length ? (
        <Curtain id="account-orders-unpaid" open={unpaidOpen}>
          <p className="account-unpaid-note">
            Срок оплаты этих заказов истёк. Через сутки они пропадут из кабинета. Чтобы купить, оформите заказ заново.
          </p>
          <ul className="account-order-list">{unpaid.map(renderOrder)}</ul>
        </Curtain>
      ) : null}
    </div>
  );
}

export default AccountOrders;
